/**
 * White-label branding (ADR-0014). Fetched once from the public `/api/branding`
 * — no session needed, so the login screen is branded too. Applies the accent
 * colour as a CSS custom property and sets the document title.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.js';
import type { Branding } from './types.js';

const FALLBACK: Branding = {
  productName: 'Akarya Signage',
  accentColor: '#2563eb',
  supportUrl: null,
  logoUrl: null,
  loginBackgroundUrl: null,
  playerSplashUrl: null,
};

interface BrandingValue {
  branding: Branding;
  /** Re-fetch after the operator changes branding in settings. */
  refresh: () => void;
}

const BrandingContext = createContext<BrandingValue | null>(null);

/** Relative luminance → readable text colour over the accent. */
function textOn(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.slice(0, 7));
  if (!m?.[1]) return '#ffffff';
  const n = parseInt(m[1], 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45 ? '#0b1017' : '#ffffff';
}

export function BrandingProvider({ children }: { children: ReactNode }): ReactNode {
  const [branding, setBranding] = useState<Branding>(FALLBACK);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    void api.branding().then(setBranding);
  }, [tick]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--accent', branding.accentColor);
    root.style.setProperty('--accent-text', textOn(branding.accentColor));
    document.title = branding.productName;
  }, [branding]);

  const value = useMemo<BrandingValue>(
    () => ({ branding, refresh: () => setTick((n) => n + 1) }),
    [branding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within <BrandingProvider>');
  return ctx;
}
